import React, { useEffect, useMemo, useRef, useState } from 'react';
import { batch } from 'react-redux';
import styled from 'styled-components';
import isNil from 'lodash/isNil';
import {
  IRowMeta,
  removeErrorAction,
  removeReroutingEventAction,
  RowType,
  setExpandEventAction,
} from '../../../../../redux/slices/oldBuilder';
import {
  errorPrimitiveSelector,
  eventsPrimitiveSelector,
  expandEventFieldPrimitiveSelector,
  filterPrimitiveSelector,
} from '../../../../../redux/selectors/builder';
import {
  BuilderFilterFieldsHighlighted,
  BuilderFilterFieldsTrigger,
} from '../../BuilderFilterFields';
import {
  IBasicEventValueFieldsFragment,
  IFilterFieldsFragment,
} from '../../../../../utils/apollo/graphqlSchema';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import ExpandEventPopover, { IExpandEventValue } from './ExpandEventPopover';
import { assertNotNil } from '../../../../../utils/types';
import { useActionsVisibilityContext } from '../../../../ActionsVisibilityContext';
import { BuilderRowActions } from '../../../BuilderActions';
import * as BuilderUI from '../../../BuilderUI';
import { FauxInput } from '../../../../Form';
import ExpandEventValue from './ExpandEventValue';
import { useHighlightingContext } from '../../../../HighlightingContext';
import { MaybeNil } from '../../../../../utils/interfaces';
import {
  EntityDropzoneDataTypeEnum,
  IEventEntityDropzoneDataType,
} from '../../../../DndEntity/validation';
import {
  createDictionaryEntityDropzoneId,
  getDndActiveItemData,
} from '../../../../DndEntity/utils';
import { EntityDropzoneType } from '../../../../QuickAccessDictionary/DictionaryTabs/types';
import DndDroppableZone from '../../../../DndEntity/DndDroppableZone';
import { useDndEntityInternalContext } from '../../../../DndEntity';
import { SquareRemoveButton } from '../../../../Button';

interface IProps {
  index: number;
}

const Container = styled.div`
  display: flex;
  align-items: flex-start;
  column-gap: 8px;
  padding-top: 4px;
  padding-bottom: 4px;
  padding-left: 4px;
  border-radius: 4px;
`;

const Label = styled(BuilderUI.InputLabel)`
  margin-top: 6px;
  line-height: normal;
`;

const MainPart = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 16px;
  row-gap: 8px;
`;

const Content = styled.div`
  flex: 1;
`;

const MenuContainer = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
`;

const ExpandEventInput = ({ index }: IProps) => {
  const dispatch = useAppDispatch();

  const rowMeta: IRowMeta = {
    rowType: RowType.expandEvents,
    rowIndex: index,
  };

  const { actionsVisible, hideActions } = useActionsVisibilityContext();
  const { activeItem } = useDndEntityInternalContext();

  const { popoverId, setPopoverId } = useDndEntityInternalContext();
  const eventsDropzoneId = createDictionaryEntityDropzoneId(
    EntityDropzoneType.Events,
    rowMeta,
  );
  const fieldsDropzoneId = createDictionaryEntityDropzoneId(
    EntityDropzoneType.Fields,
    rowMeta,
  );

  const { activateHighlighting, deactivateHighlighting } =
    useHighlightingContext();

  const filterSelector = useMemo(filterPrimitiveSelector, []);
  const eventFilter: MaybeNil<IFilterFieldsFragment> = useAppSelector((state) =>
    filterSelector(state, rowMeta),
  );

  const eventsSelector = useMemo(eventsPrimitiveSelector, []);
  const fieldSelector = useMemo(expandEventFieldPrimitiveSelector, []);
  const [basicEvents] = useAppSelector((state) =>
    eventsSelector(state, rowMeta),
  );
  const basicEvent =
    (basicEvents?.[0] as IBasicEventValueFieldsFragment) ?? null;
  const eventField = useAppSelector((state) => fieldSelector(state, rowMeta));
  const errorSelector = useMemo(errorPrimitiveSelector, []);
  const errors = useAppSelector((state) => errorSelector(state, rowMeta, ''));

  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  // if the popoverId from dnd context changes, and the id is
  // the same as the eventsDropzoneId, open the popover.
  useEffect(() => {
    if (!isNil(popoverId) && popoverId === eventsDropzoneId) {
      setPopoverOpen(true);
    }
  }, [popoverId, eventsDropzoneId]);

  const handleOnChange = (expandEvent: IExpandEventValue | null) => {
    batch(() => {
      dispatch(removeErrorAction({ rowMeta, errorKey: '' }));
      if (!isNil(expandEvent)) {
        const { event, field } = expandEvent;
        assertNotNil(event);
        assertNotNil(field);

        dispatch(
          setExpandEventAction({
            rowMeta,
            expandEvent: {
              field,
              event,
            },
          }),
        );
      } else {
        dispatch(
          setExpandEventAction({
            rowMeta,
            expandEvent: {
              field: null,
              event: null,
            },
          }),
        );
      }
    });
    onClose();
  };

  const handleRemove = () => {
    batch(() => {
      dispatch(
        removeReroutingEventAction({
          index: rowMeta.rowIndex,
          type: RowType.expandEvents,
        }),
      );
      dispatch(removeErrorAction({ rowMeta, errorKey: '', clearRow: true }));
    });
  };

  const handleInputClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event?.defaultPrevented) {
      return;
    }

    hideActions();

    setPopoverOpen(true);
  };

  const onClose = () => {
    setPopoverOpen(false);
    if (popoverId) {
      setPopoverId(null);
    }
  };

  const showDropzone = useMemo(() => {
    const data = getDndActiveItemData(activeItem);
    if (data) {
      return data.__typename === 'TagLeafBasicEvent';
    }

    return false;
  }, [activeItem]);

  return (
    <Container>
      <Label>Expand Event</Label>

      <Content>
        <MainPart>
          <MenuContainer>
            <DndDroppableZone
              accepts={[EntityDropzoneType.Events]}
              id={eventsDropzoneId}
              data={
                {
                  id: eventsDropzoneId,
                  type: EntityDropzoneDataTypeEnum.Events,
                  rowMeta,
                } satisfies IEventEntityDropzoneDataType
              }
              isDisabled={!showDropzone}
            >
              <FauxInput
                ref={anchorRef}
                hasError={errors}
                onClick={handleInputClick}
              >
                <ExpandEventValue
                  event={basicEvent}
                  field={eventField}
                  onClear={() => handleOnChange(null)}
                  placeholder={'Select event to expand'}
                />
              </FauxInput>
            </DndDroppableZone>
            {isPopoverOpen && (
              <ExpandEventPopover
                isOpen={isPopoverOpen}
                onClose={onClose}
                value={{
                  event: basicEvent,
                  field: eventField,
                }}
                anchorEl={anchorRef}
                onChange={handleOnChange}
              />
            )}
          </MenuContainer>
          <BuilderFilterFieldsTrigger
            rowMeta={rowMeta}
            fieldsDropzoneId={fieldsDropzoneId}
          />

          <BuilderRowActions>
            {actionsVisible && (
              <SquareRemoveButton
                onClick={handleRemove}
                content={'Remove Expand Event'}
                onMouseEnter={activateHighlighting}
                onMouseLeave={deactivateHighlighting}
              />
            )}
          </BuilderRowActions>
        </MainPart>
        {eventFilter?.conditions?.length && (
          <BuilderFilterFieldsHighlighted
            rowMeta={rowMeta}
            filter={eventFilter}
          />
        )}
      </Content>
    </Container>
  );
};

export default ExpandEventInput;
